<template>
  <div
    v-if="show"
    class="animation-player fixed inset-0 bg-base-300/90 z-50 flex items-center justify-center"
  >
    <!-- 关闭按钮 -->
    <button
      class="absolute top-4 right-4 btn btn-circle btn-lg btn-primary"
      @click="handleClose"
    >
      ✕
    </button>

    <!-- 动画显示区域 -->
    <div class="flex flex-col items-center space-y-4">
      <!-- 动画画布 -->
      <div class="animation-canvas bg-base-100 rounded-lg p-4 shadow-xl">
        <canvas
          ref="canvasRef"
          :width="canvasWidth"
          :height="canvasHeight"
          class="pixelated"
        ></canvas>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";
import type { Frame } from "./types";
import { CANVAS_CONFIG } from "./config";

interface Props {
  show: boolean;
  frames: Frame[];
}

interface Emits {
  (e: "close"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// 状态
const canvasRef = ref<HTMLCanvasElement>();
const isPlaying = ref(false);
const currentFrameIndex = ref(0);
const fps = ref(5);
const animationTimer = ref<ReturnType<typeof setInterval> | null>(null);

// 计算动画画布尺寸（放大显示）
const scaleFactor = 4;
const canvasWidth = CANVAS_CONFIG.SPRITE_WIDTH * scaleFactor;
const canvasHeight = CANVAS_CONFIG.SPRITE_HEIGHT * scaleFactor;

// 开始播放动画
const startAnimation = () => {
  if (props.frames.length === 0) return;

  isPlaying.value = true;
  const interval = 1000 / fps.value; // 毫秒

  animationTimer.value = setInterval(() => {
    currentFrameIndex.value =
      (currentFrameIndex.value + 1) % props.frames.length;
    drawCurrentFrame();
  }, interval);
};

// 停止动画
const stopAnimation = () => {
  if (animationTimer.value) {
    clearInterval(animationTimer.value);
    animationTimer.value = null;
  }
  isPlaying.value = false;
  currentFrameIndex.value = 0;
  drawCurrentFrame();
};

// 切换播放/暂停
// const togglePlayPause = () => {
//   if (isPlaying.value) {
//     // 暂停
//     if (animationTimer.value) {
//       clearInterval(animationTimer.value);
//       animationTimer.value = null;
//     }
//     isPlaying.value = false;
//   } else {
//     // 播放
//     startAnimation();
//   }
// };

// // 更新帧率
// const updateFrameRate = () => {
//   if (isPlaying.value) {
//     // 重新启动动画以应用新帧率
//     stopAnimation();
//     startAnimation();
//   }
// };

// 预加载所有图片
const preloadedImages = ref<HTMLImageElement[]>([]);

// 预加载图片
const preloadImages = async () => {
  preloadedImages.value = await Promise.all(
    props.frames.map((frame) => {
      return new Promise<HTMLImageElement>((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.src = frame.url;
      });
    })
  );
};

// 绘制当前帧
const drawCurrentFrame = () => {
  if (!canvasRef.value || !preloadedImages.value[currentFrameIndex.value])
    return;

  const canvas = canvasRef.value;
  const ctx = canvas.getContext("2d")!;

  // 设置像素化渲染
  ctx.imageSmoothingEnabled = false;

  // 清空画布并立即绘制新图片
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const img = preloadedImages.value[currentFrameIndex.value];
  ctx.drawImage(
    img,
    0,
    0,
    CANVAS_CONFIG.SPRITE_WIDTH,
    CANVAS_CONFIG.SPRITE_HEIGHT,
    0,
    0,
    canvasWidth,
    canvasHeight
  );
};

// 关闭播放器
const handleClose = () => {
  stopAnimation();
  emit("close");
};

// 监听show变化
watch(
  () => props.show,
  async (newShow) => {
    if (newShow) {
      await nextTick();
      await preloadImages();
      drawCurrentFrame();
      startAnimation();
    } else {
      stopAnimation();
    }
  }
);

// 监听帧数据变化
watch(
  () => props.frames,
  async () => {
    if (props.show) {
      currentFrameIndex.value = 0;
      await preloadImages();
      drawCurrentFrame();
    }
  },
  { deep: true }
);

// 组件卸载时清理
onUnmounted(() => {
  stopAnimation();
});

// ESC键关闭
const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === "Escape" && props.show) {
    handleClose();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeyDown);
});
</script>

<style scoped>
.animation-player {
  backdrop-filter: blur(4px);
}

.pixelated {
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.animation-canvas {
  box-shadow: 0 0 30px hsl(var(--bc) / 0.2);
}
</style>
